export const REDACTED_URL = '[redacted-url]'
export const REDACTED_TOKEN = '[redacted-token]'
export const REDACTED_ID = '[redacted-id]'
export const REDACTED_EMAIL = '[redacted-email]'

// Everything not listed here is dropped. An allowlist rather than a denylist because
// posthog-js keeps adding default properties, and a denylist is only ever correct for
// the properties that existed when it was written.
const ALLOWED_KEYS = new Set([
  'client_id',
  'session_id',
  'environment_tier',
  'deployment_id',
  'route_name',
  'route_pattern',
  'navigation_type',
  'tab',
  'widget',
  'role',
  'user_rights',
  'language',
  'duration_ms',
  'end_reason',
  '$lib',
  '$lib_version',
  '$insert_id',
  '$time',
  '$sent_at',
  '$session_id',
  '$window_id',
  '$device_id',
  'distinct_id',
  'token',
  '$groups',
  '$group_type',
  '$group_key',
  '$group_set',
  '$is_identified',
  '$anon_distinct_id',
  '$process_person_profile',
  '$geoip_disable',
  '$set',
  '$set_once',
  '$os',
  '$os_version',
  '$browser',
  '$browser_version',
  '$device_type',
  '$screen_height',
  '$screen_width',
  '$viewport_height',
  '$viewport_width'
])

const ALLOWED_PERSON_KEYS = new Set(['role', 'user_rights'])

// Values we construct ourselves. They are shape-validated instead of redacted, because
// route patterns look exactly like the paths the redactor is meant to strip.
const CONSTRUCTED_KEYS = new Map<string, RegExp>([
  ['route_pattern', /^\/[A-Za-z0-9\-_/:?]*$/],
  ['route_name', /^[a-z0-9-]{1,60}$/i],
  ['navigation_type', /^(initial|in_app)$/],
  ['tab', /^[a-z0-9_-]{1,40}$/i],
  ['widget', /^[a-z0-9_-]{1,40}$/i],
  ['client_id', /^[A-Za-z0-9-]{1,64}$/],
  ['session_id', /^[A-Za-z0-9-]{1,64}$/],
  ['environment_tier', /^[a-z0-9-]{1,40}$/],
  ['deployment_id', /^[a-z0-9-]{1,40}$/],
  ['end_reason', /^[a-z_]{1,20}$/]
])

// Coarse browser/device facts posthog-js computes itself on every event - not app-constructed
// like the keys above, not opaque identifiers either, but worth the same treatment: shape-
// validated rather than run through the generic redactor, since a bare word like "Chrome" or
// "iOS" would otherwise pass redact() unexamined anyway. Deliberately excludes $raw_user_agent
// (a near-unique fingerprint), $device (can be a specific hardware model), $timezone and
// $browser_language (an app-level `language` key already covers UI locale) - all stay dropped
// by the allowlist above.
const DEVICE_KEYS = new Map<string, RegExp>([
  ['$os', /^[A-Za-z0-9 ./()-]{1,40}$/],
  ['$os_version', /^[A-Za-z0-9 ./()-]{1,40}$/],
  ['$browser', /^[A-Za-z0-9 ./()-]{1,40}$/],
  ['$browser_version', /^[A-Za-z0-9.]{1,20}$/],
  ['$device_type', /^[A-Za-z]{1,20}$/]
])

// Screen/viewport dimensions - always numbers from the SDK, so redact() would let them through
// unchanged anyway (it only touches strings), but bounding them keeps a nonsensical value
// (negative, NaN, a bug in a future SDK build) from reaching PostHog rather than just trusting it.
const DIMENSION_KEYS = new Set([
  '$screen_height',
  '$screen_width',
  '$viewport_height',
  '$viewport_width'
])
const MAX_DIMENSION_PX = 20000

// Opaque identifiers PostHog needs intact for attribution - person, device, session,
// window, event, project. Only distinct_id identifies the user; the others are PostHog's
// own bookkeeping ids. They are grouped because they need the same treatment:
// shape-validated rather than redacted, since running the UUID rule over distinct_id
// collapses every user into one person called "[redacted-id]", and a redacted token
// makes PostHog reject the event outright.
const OPAQUE_ID = /^[A-Za-z0-9_-]{8,128}$/
const POSTHOG_ID_KEYS = new Set([
  'distinct_id',
  '$device_id',
  '$anon_distinct_id',
  '$session_id',
  '$window_id',
  '$insert_id',
  'token'
])

const ABSOLUTE_URL = /https?:\/\//i
const PATH_LIKE = /(^|\s)\/[^\s?#]/
const EMAIL = /\S+@\S+\.\S+/
// urlSafeBase64(userName) is a long unpadded base64url run. A UUID-shaped rule does
// not catch it, and base64 is reversible, so it must be redacted explicitly.
const OPAQUE_TOKEN = /[A-Za-z0-9_-]{16,}(?:={1,2}|%3D{1,2})/gi
const UUID = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi

const MAX_DEPTH = 6

export function sanitizeProperties(
  properties?: Record<string, unknown> | null
): Record<string, unknown> {
  if (!properties) {
    return {}
  }

  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(properties)) {
    if (!ALLOWED_KEYS.has(key)) {
      continue
    }

    if (key === '$set' || key === '$set_once') {
      const person = sanitizePersonProperties(value)
      if (Object.keys(person).length > 0) {
        result[key] = person
      }
      continue
    }

    const constraint = CONSTRUCTED_KEYS.get(key)
    if (constraint !== undefined) {
      if (typeof value === 'string' && constraint.test(value)) {
        result[key] = value
      }
      continue
    }

    const deviceConstraint = DEVICE_KEYS.get(key)
    if (deviceConstraint !== undefined) {
      if (typeof value === 'string' && deviceConstraint.test(value)) {
        result[key] = value
      }
      continue
    }

    if (DIMENSION_KEYS.has(key)) {
      if (
        typeof value === 'number' &&
        Number.isFinite(value) &&
        value >= 0 &&
        value <= MAX_DIMENSION_PX
      ) {
        result[key] = value
      }
      continue
    }

    if (POSTHOG_ID_KEYS.has(key)) {
      if (typeof value === 'string' && OPAQUE_ID.test(value)) {
        result[key] = value
      }
      continue
    }

    if (key === '$groups') {
      const groups = sanitizeGroups(value)
      if (Object.keys(groups).length > 0) {
        result[key] = groups
      }
      continue
    }

    result[key] = redact(value, 0)
  }

  return result
}

function sanitizeGroups(value: unknown): Record<string, string> {
  if (!isPlainObject(value)) {
    return {}
  }

  const result: Record<string, string> = {}
  for (const [groupType, groupKey] of Object.entries(value)) {
    if (typeof groupKey === 'string' && OPAQUE_ID.test(groupKey)) {
      result[groupType] = groupKey
    }
  }
  return result
}

function sanitizePersonProperties(value: unknown): Record<string, unknown> {
  if (!isPlainObject(value)) {
    return {}
  }

  const result: Record<string, unknown> = {}
  for (const [key, nested] of Object.entries(value)) {
    if (ALLOWED_PERSON_KEYS.has(key)) {
      result[key] = redact(nested, 0)
    }
  }
  return result
}

function redact(value: unknown, depth: number): unknown {
  if (typeof value === 'string') {
    return redactString(value)
  }

  if (depth >= MAX_DEPTH) {
    return null
  }

  if (Array.isArray(value)) {
    return value.map((entry) => redact(entry, depth + 1))
  }

  if (isPlainObject(value)) {
    const result: Record<string, unknown> = {}
    for (const [key, nested] of Object.entries(value)) {
      result[key] = redact(nested, depth + 1)
    }
    return result
  }

  return value
}

function redactString(value: string): string {
  if (ABSOLUTE_URL.test(value) || PATH_LIKE.test(value)) {
    return REDACTED_URL
  }

  return value
    .replace(EMAIL, REDACTED_EMAIL)
    .replace(UUID, REDACTED_ID)
    .replace(OPAQUE_TOKEN, REDACTED_TOKEN)
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
